import {
  Box,
  TextField,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Switch,
} from '@mui/material';
import { useState, useEffect } from 'react';

import { createApplication, updateApplication } from '../../services/applicationService';
import { getRoles } from '../../services/roleService';

const toDateTimeLocalValue = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const expirationPresets = {
  '1-week': { days: 7 },
  '1-month': { months: 1 },
  '1-year': { years: 1 },
};

const getPresetExpiration = mode => {
  const preset = expirationPresets[mode];
  if (!preset) return null;

  const expiresAt = new Date();
  if (preset.days) expiresAt.setDate(expiresAt.getDate() + preset.days);
  if (preset.months) expiresAt.setMonth(expiresAt.getMonth() + preset.months);
  if (preset.years) expiresAt.setFullYear(expiresAt.getFullYear() + preset.years);
  return expiresAt.toISOString();
};

const ApplicationForm = ({ application, onSubmitted, onCancel }) => {
  const [formData, setFormData] = useState({
    name: application?.name || '',
    description: application?.description || '',
    apiKey: application?.apiKey || '',
    defaultRole: application?.defaultRole?._id || '',
    isActive: application?.isActive ?? true,
    expiresAt: toDateTimeLocalValue(application?.expiresAt),
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useCustomApiKey, setUseCustomApiKey] = useState(false);
  const [expirationMode, setExpirationMode] = useState(application?.expiresAt ? 'custom' : 'never');

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await getRoles();
        setRoles(data);
      } catch (err) {
        setError('Failed to fetch roles');
      }
    };
    fetchRoles();
  }, []);

  const handleChange = e => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = { ...formData };
      if (!useCustomApiKey || !submitData.apiKey) {
        delete submitData.apiKey;
      }

      const presetExpiration = getPresetExpiration(expirationMode);

      if (presetExpiration) {
        submitData.expiresAt = presetExpiration;
      } else if (expirationMode === 'custom' && submitData.expiresAt) {
        submitData.expiresAt = new Date(submitData.expiresAt).toISOString();
      } else if (application) {
        submitData.expiresAt = null;
      } else {
        delete submitData.expiresAt;
      }

      if (application) {
        await updateApplication(application._id, submitData);
      } else {
        await createApplication(submitData);
      }
      onSubmitted();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <DialogTitle>{application ? 'Edit Application' : 'Create New Application'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          label="Application Name"
          name="name"
          fullWidth
          required
          value={formData.name}
          onChange={handleChange}
        />

        <TextField
          margin="dense"
          label="Description"
          name="description"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange}
        />

        {!application && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomApiKey}
                  onChange={e => {
                    setUseCustomApiKey(e.target.checked);
                    if (!e.target.checked) {
                      setFormData(prev => ({ ...prev, apiKey: '' }));
                    }
                  }}
                />
              }
              label="Specify custom API key"
            />
          </Box>
        )}

        {(useCustomApiKey || application) && (
          <TextField
            margin="dense"
            label="API Key"
            name="apiKey"
            fullWidth
            value={formData.apiKey}
            onChange={handleChange}
            helperText={
              useCustomApiKey
                ? 'Enter a custom API key or leave blank to auto-generate.'
                : 'API key cannot be changed after creation.'
            }
            disabled={!!application}
            placeholder={useCustomApiKey ? 'e.g., mapi_your_custom_key_here' : ''}
          />
        )}

        <TextField
          select
          margin="dense"
          label="API Key Expiration"
          fullWidth
          value={expirationMode}
          onChange={e => {
            setExpirationMode(e.target.value);
            if (e.target.value === 'never') {
              setFormData(prev => ({ ...prev, expiresAt: '' }));
            }
          }}
        >
          <MenuItem value="never">Never expires</MenuItem>
          <MenuItem value="1-week">1 week</MenuItem>
          <MenuItem value="1-month">1 month</MenuItem>
          <MenuItem value="1-year">1 year</MenuItem>
          <MenuItem value="custom">Custom date and time</MenuItem>
        </TextField>

        {expirationMode === 'custom' && (
          <TextField
            margin="dense"
            label="Expires At"
            name="expiresAt"
            type="datetime-local"
            fullWidth
            required
            value={formData.expiresAt}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        )}

        <TextField
          select
          margin="dense"
          label="Default Role"
          name="defaultRole"
          fullWidth
          required
          value={formData.defaultRole}
          onChange={handleChange}
        >
          {roles.map(role => (
            <MenuItem key={role._id} value={role._id}>
              {role.name}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={<Checkbox checked={formData.isActive} onChange={handleChange} name="isActive" />}
          label="Active"
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {application ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default ApplicationForm;
